import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { downloadBlob } from "@/foundation/downloads";
import { formatReleaseDateIT } from "@/lib/dates";

import { GuideSidebar } from "./GuideSidebar";
import { GuideSearch } from "./GuideSearch";
import { GuideContent } from "./GuideContent";
import { GuideBreadcrumb } from "./GuideBreadcrumb";
import { GuideAskAI } from "./GuideAskAI";
import { GuideHomeCards } from "./GuideHomeCards";
import { useGuidePack } from "./hooks/useGuidePack";
import { useGuideSearch } from "./hooks/useGuideSearch";
import { useGuideContext } from "./hooks/useGuideContext";
import { useGuideFeedbackSummaries } from "./hooks/useGuideFeedbackSummaries";

/**
 * Centro Guida - Shell principale della Foundation.
 *
 * E' il componente riutilizzabile da tutti i moduli della Suite: prende un
 * ``appKey`` e renderizza sidebar, ricerca, contenuti, breadcrumb, PDF,
 * placeholder AI.
 *
 * Props:
 *   - appKey (obbligatorio): chiave Pack (es. "presenze")
 *   - aiEnabled (default false): mostra il placeholder AI
 *   - backTo (default "/"): destinazione della freccia indietro
 */
export function GuideShell({ appKey, aiEnabled = false, backTo = "/" }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const referrerRef = useRef(null);
  useEffect(() => {
    // Se e' il primo mount, salviamo la route "referrer" per la contestualita'.
    // In caso di apertura diretta di /guida senza referrer, useGuideContext
    // fallira' silenziosamente e mostreremo il primo capitolo.
    const fromState = location.state?.from;
    if (fromState && !referrerRef.current) referrerRef.current = fromState;
    // Nota: document.referrer non affidabile perche' SPA.
  }, [location]);

  const topicParam = searchParams.get("topic") || null;
  const sectionParam = searchParams.get("section") || null;

  const { topic: resolvedTopic } = useGuideContext(
    appKey,
    location.pathname,
    referrerRef.current,
  );
  const effectiveTopic = topicParam || resolvedTopic || null;

  const { pack, loading } = useGuidePack(appKey, { topic: effectiveTopic });

  const chapters = useMemo(() => pack?.chapters || [], [pack]);
  const [expanded, setExpanded] = useState({});
  const [activeChapterId, setActiveChapterId] = useState(null);
  const [activeArticleId, setActiveArticleId] = useState(null);

  // Lista articleId per il bulk fetch feedback (una sola chiamata)
  const allArticleIds = useMemo(
    () => chapters.flatMap((c) => c.articles.map((a) => a.id)),
    [chapters],
  );
  const { summaries: feedbackSummaries } = useGuideFeedbackSummaries(
    appKey,
    allArticleIds,
  );

  // Auto-espansione del capitolo relativo al topic contestuale
  useEffect(() => {
    if (!chapters.length) return;
    const targetChapter =
      chapters.find((c) => c.topic === effectiveTopic) || chapters[0];
    if (!targetChapter) return;
    setExpanded((prev) => {
      if (prev[targetChapter.id]) return prev;
      return { ...prev, [targetChapter.id]: true };
    });
    setActiveChapterId(targetChapter.id);
    // Scroll dopo il paint
    const scrollTarget = sectionParam
      ? `guide-art-${appKey}.${sectionParam}`
      : `guide-ch-${targetChapter.id}`;
    // Attende il render prima di scrollare
    setTimeout(() => {
      const el = document.getElementById(scrollTarget);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-suite-primary/40");
        setTimeout(
          () => el.classList.remove("ring-2", "ring-suite-primary/40"),
          1400,
        );
      }
    }, 150);
    if (sectionParam) {
      setActiveArticleId(`${appKey}.${sectionParam}`);
    }
  }, [chapters, effectiveTopic, sectionParam, appKey]);

  const toggleChapter = useCallback((chapterId) => {
    setExpanded((prev) => ({ ...prev, [chapterId]: !prev[chapterId] }));
    setActiveChapterId(chapterId);
  }, []);

  const selectChapter = useCallback((chapterId) => {
    setExpanded((prev) => ({ ...prev, [chapterId]: true }));
    setActiveChapterId(chapterId);
    setTimeout(() => {
      const el = document.getElementById(`guide-ch-${chapterId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 30);
  }, []);

  const selectArticle = useCallback(
    (chapterId, articleId) => {
      setExpanded((prev) => ({ ...prev, [chapterId]: true }));
      setActiveChapterId(chapterId);
      setActiveArticleId(articleId);
      setTimeout(() => {
        const el = document.getElementById(`guide-art-${articleId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.classList.add("ring-2", "ring-suite-primary/40");
          setTimeout(
            () => el.classList.remove("ring-2", "ring-suite-primary/40"),
            1400,
          );
        }
      }, 30);
      // Deep-link condivisibile nella URL
      const nextParams = new URLSearchParams(searchParams);
      const slug = articleId.startsWith(`${appKey}.`)
        ? articleId.slice(appKey.length + 1)
        : articleId;
      nextParams.set("section", slug);
      setSearchParams(nextParams, { replace: true });
    },
    [appKey, searchParams, setSearchParams],
  );

  // Ricerca
  const [query, setQuery] = useState("");
  const { results, loading: searching } = useGuideSearch(appKey, query);

  const onSelectSearchHit = useCallback(
    (chapterId, articleId) => {
      setQuery("");
      selectArticle(chapterId, articleId);
    },
    [selectArticle],
  );

  const activeChapterTitle = useMemo(() => {
    if (!activeChapterId) return null;
    return chapters.find((c) => c.id === activeChapterId)?.title || null;
  }, [activeChapterId, chapters]);

  const downloadPdf = async () => {
    try {
      const resp = await api.get("/guide/pdf", {
        params: { app: appKey },
        responseType: "blob",
      });
      const filename = `Manuale_${pack?.app_label?.replace(/ /g, "_") || appKey}.pdf`;
      downloadBlob(resp.data, filename, "application/pdf");
    } catch {
      const base = api.defaults.baseURL || "/api";
      window.open(`${base}/guide/pdf?app=${encodeURIComponent(appKey)}`, "_blank");
    }
  };

  if (loading && !pack) {
    return (
      <div
        className="p-8 text-sm text-suite-neutral-500"
        data-testid="guide-loading"
      >
        Caricamento del Centro Guida...
      </div>
    );
  }
  if (!pack) {
    return (
      <div
        className="p-8 text-sm text-suite-error"
        data-testid="guide-error"
      >
        Impossibile caricare il Centro Guida.
      </div>
    );
  }

  return (
    <div
      className="mx-auto max-w-7xl px-4 py-6 lg:px-8"
      data-testid="guide-shell"
    >
      {/* Testata */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to={backTo}
            className="text-suite-neutral-500 hover:text-suite-primary"
            title="Torna indietro"
            data-testid="guide-back"
          >
            <ArrowLeft size={18} />
          </Link>
          <BookOpen className="text-suite-primary" size={22} />
          <div>
            <h1
              className="text-xl font-bold text-suite-neutral-900"
              data-testid="guide-title"
            >
              Centro Guida
            </h1>
            <p className="text-[11px] text-suite-neutral-500">
              {pack.app_label} · versione applicativa{" "}
              <b>{pack.version_label}</b> · ultimo aggiornamento{" "}
              <b>{formatReleaseDateIT(pack.updated_at)}</b> · profilo{" "}
              <b>{pack.role_label}</b>
            </p>
            <GuideBreadcrumb
              appLabel={pack.app_label}
              activeChapterTitle={activeChapterTitle}
            />
          </div>
        </div>
        <Button
          size="sm"
          onClick={downloadPdf}
          data-testid="guide-download-pdf"
          className="bg-suite-primary text-white hover:bg-suite-primary/90"
        >
          <Download size={14} className="mr-1.5" /> Scarica manuale PDF
        </Button>
      </div>

      {/* Ricerca */}
      <div className="mb-4">
        <GuideSearch
          value={query}
          onChange={setQuery}
          results={results}
          loading={searching}
          onSelect={onSelectSearchHit}
        />
      </div>

      {/* Card Operative Foundation - Home del Centro Guida */}
      <div className="mb-6" data-testid="guide-home-cards-wrapper">
        <GuideHomeCards role={pack.role} packs={["foundation", appKey]} />
      </div>

      {/* Layout principale */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        <div className="space-y-3">
          <GuideSidebar
            chapters={chapters}
            expanded={expanded}
            onToggleChapter={toggleChapter}
            onSelectChapter={selectChapter}
            onSelectArticle={selectArticle}
            activeChapterId={activeChapterId}
            activeArticleId={activeArticleId}
          />
          <GuideAskAI enabled={aiEnabled} />
          <div className="rounded-lg border border-suite-neutral-200 bg-suite-neutral-50 p-3 text-[10px] leading-snug text-suite-neutral-500">
            &copy; {pack.copyright?.owner}
            <br />
            C.F./P.IVA {pack.copyright?.vat}
            <br />
            <a
              href={`mailto:${pack.copyright?.email}`}
              className="text-suite-primary hover:underline"
            >
              {pack.copyright?.email}
            </a>
          </div>
        </div>
        <GuideContent
          appKey={appKey}
          chapters={chapters}
          expanded={expanded}
          onToggleChapter={toggleChapter}
          feedbackSummaries={feedbackSummaries}
          packVersion={pack.version_label}
        />
      </div>
    </div>
  );
}
